<template>
  <div class="upcoming-event" :class="elClasses">
    <div class="upcoming-event__wrapper generic-border" @click="openModal">
      <div class="upcoming-event__name">
        {{ event.name }}
      </div>
      <div class="upcoming-event__timer">
        {{ timerDisplay }}
      </div>
    </div>

    <SlideFadeModal
      ref="modal"
      class="upcoming-event__modal"
      :displayed="modalDisplayed"
      @outsideClick="closeModal"
    >
      <div v-if="modalDisplayed" class="generic-border" @click.stop="">
        <ScheduleEvent :event="scheduleEvent" />
        <div v-if="event.type === 1" class="filters-zone__modal__sep " />
        <MeetingPanelContent v-if="scheduleEvent.type === 1" :meeting="scheduleEvent" />
        <div class="v-centerer upcoming-event__modal__start">
          <button
            class="generic-border generic-border--text-btn generic-border--blue"
            @click="startButtonClicked"
          >
            {{ $t('message.START_NOW') }}
          </button>
        </div>
      </div>
    </SlideFadeModal>
  </div>
</template>

<script>
/**
 * Event item in a list of upcoming events.
 *
 * @vue-prop {Object} event - The event object. Either a GW or a meeting
 */

import toastr from 'toastr';
// Vuex
import { START_MEETING } from '@/store/modules/meeting/action-types';
import { START_GW } from '@/store/modules/gemba-walks/action-types';
// Components
import MeetingPanelContent from '@/components/meeting-panel-content.vue';
import ScheduleEvent from '@/components/schedule-event.vue';
import SlideFadeModal from '@/components/edition-modals/slide-fade-modal.vue';
// Utils
import { secondsToArray } from '@/utils/main';

export default {

  name: 'UpcomingEvent',

  components: {
    MeetingPanelContent,
    ScheduleEvent,
    SlideFadeModal,
  },

  props: {
    event: {
      type: Object,
      required: true,
    },
  },

  data() {
    return {
      timeDiff: 0,
      timerIntervalTask: null,
      modalDisplayed: false,
    };
  },

  computed: {
    /** Object: CSS classes applied to this.$el */
    elClasses() {
      return {
        'upcoming-event--type-meeting': this.event.gw === undefined,
        'upcoming-event--type-gw': this.event.gw !== undefined,
      };
    },
    /** String: Time to the event */
    timerDisplay() {
      const time = secondsToArray(this.timeDiff);
      return `${time[0]}:${time[1]}:${time[2]}`;
    },
    /** Moment: Instance of the date of the event */
    momentMeetingDate() {
      return this.$moment(this.event.date);
    },
    /** Object: Make this.event compatible with a `type` and a `color` props */
    scheduleEvent() {
      return {
        ...this.event,
        type: this.event.gw === undefined ? 1 : 2,
        color: 'pdcaDo',
      };
    },
  },

  mounted() {
    this.timerIntervalTask = setInterval(() => {
      // Display the timer
      this.timeDiff = this.momentMeetingDate.diff(this.$moment(), 's');
    }, 1000);
    // Keep the modal above everything else
    this.documentBody.appendChild(this.$refs.modal.$el);
  },

  beforeDestroy() {
    if (this.timerIntervalTask) {
      clearInterval(this.timerIntervalTask);
    }
    if (this.$refs.modal && this.$refs.modal.$el) {
      this.$refs.modal.$el.remove();
    }
  },

  methods: {
    openModal() {
      this.modalDisplayed = true;
    },
    closeModal() {
      this.modalDisplayed = false;
    },
    /** Event has been clicked then the 'start' button was clicked */
    startButtonClicked() {
      if (this.scheduleEvent.type === 1) {
        // Meeting
        this.startMeeting();
      } else if (this.scheduleEvent.type === 2) {
        // GW
        this.startGw();
      }
      this.closeModal();
    },
    startGw() {
      this.$store.dispatch(`gembaWalks/${START_GW}`, { template: this.scheduleEvent.gw }).then((response) => {
        if (response && response.data instanceof Object && response.data.id) {
          toastr.info(this.$t('message.STARTING'));
          this.$router.push(this.getGwOngoingLink(response.data.id));
        }
      });
    },
    /** Start a meeting */
    startMeeting() {
      this.$store.dispatch(`meeting/${START_MEETING}`, { meeting: this.scheduleEvent });
    },
  },
};
</script>

<style lang="scss">
@import "@/assets/scss/vars.scss";

.upcoming-event {

  &--type-meeting {
    .upcoming-event__wrapper {
      background: $color-pdca-do-hover;
    }
  }

  &--type-gw {
    .upcoming-event__wrapper {
      background: $color-pdca-check-hover;
    }
  }

  &__wrapper {
    padding: .3rem .6rem;

    &:hover {
      background: $main-hover-color;
      cursor: pointer;
    }
  }

  &__name {
    font-weight: 600;
    min-height: 1.3rem;
  }

  &__modal {
    &.slide-fade-modal {
      .slide-fade-modal__content {
        width: 35%;
        margin-left: 65%;
      }
    }

    &__start {
      margin-top: 2rem;
      justify-content: flex-end;
    }
  }
}

@media (max-width: $smartphone-landscape) {
  .upcoming-event {
    &__modal {
      &.slide-fade-modal {
        .slide-fade-modal__content {
          width: 75%;
          margin-left: 25%;
        }
      }
    }
  }
}
</style>
